import type { OpenSource } from '@parcel/analytics';
import { capture } from '@parcel/analytics/browser';
import { AppLogo } from '@parcel/ui/app-logo';
import { Briefcase, Tag, User } from 'lucide-react';
import * as React from 'react';
import { primaryAccountType } from '../../lib/accounts.js';
import { useCaptureOnce } from '../../lib/analytics.js';
import {
  type ApiAccountType,
  type ApiContactDetail,
  type ApiContactProject,
  useContactDetail,
  useContactProjects,
} from '../../lib/contacts.js';
import { contactsCountShown, useAccountContactsWithReach } from '../../lib/enrich.js';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import { buildReportContext } from '../../lib/feedback.js';
import type { AccountRow } from '../../lib/mock-accounts.js';
import { type ProjectRow, UNKNOWN_PROJECT_TYPE, stageMeta } from '../../lib/projects-display.js';
import { AccountContactsTab } from '../accounts/account-contacts-tab.js';
import { EnrichScopeProvider } from '../enrich/enrich-scope.js';
import { ListSkeleton, SectionSkeleton } from '../entity-drawer/detail-skeletons.js';
import {
  EntityDetailLayout,
  type RecordDetailChromeProps,
} from '../entity-drawer/entity-detail-layout.js';
import { EmptyTabState } from '../entity-drawer/entity-drawer-sections.js';
import { EntityAvatar, EntityDrawerHeader } from '../entity-drawer/entity-drawer-shell.js';
import {
  type EvidenceRowItem,
  ExpandableEvidenceList,
} from '../entity-drawer/expandable-evidence-list.js';
import { getProjectTypeIcon } from '../entity-drawer/project-type-icon.js';
import { ReportDataIssueDialog, ReportIssueButton } from '../feedback/report-data-issue-dialog.js';
import { ProjectSourceAvatar } from '../projects/project-source-avatar.js';
import { StatusBadge } from '../projects/status-badge.js';
import { ContactAvatar } from './contact-avatar.js';
import { ContactExperience } from './contact-experience.js';
import {
  ContactAboutSection,
  ContactAccountSection,
  ContactDetailsSection,
} from './contact-overview-sections.js';
import { ContactRoleBadges } from './contact-role-badges.js';

interface ContactDetailProps extends RecordDetailChromeProps {
  contactId: string;
  workspaceId: string | undefined;
  source: OpenSource;
}

const FILLER = new Set([
  'at',
  'of',
  'the',
  'and',
  'for',
  'with',
  'inc',
  'llc',
  'llp',
  'ltd',
  'co',
  'corp',
  'company',
  'group',
]);
const words = (value: string | null) =>
  (value ?? '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);

/** A headline that only restates the title and the firm ("Project Manager at Coneco") says nothing new. */
export function headlineAddsSomething(
  headline: string,
  title: string | null,
  firm: string | null,
): boolean {
  const known = new Set([...words(title), ...words(firm)]);
  return words(headline).some((word) => !known.has(word) && !FILLER.has(word));
}

export const CONTACT_DETAIL_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'experience', label: 'Experience' },
  { value: 'projects', label: 'Projects' },
];

export function ContactDetail({
  contactId,
  workspaceId,
  source,
  mode,
  onToggleMode,
  backLabel,
  onBack,
  onPrev,
  onNext,
  onNavigate,
}: ContactDetailProps) {
  const detailQuery = useContactDetail(contactId, workspaceId);
  const projectsQuery = useContactProjects(contactId, workspaceId);
  const colleaguesQuery = useAccountContactsWithReach(
    detailQuery.data?.contact.account_id ?? null,
    workspaceId,
    false,
  );

  useCaptureOnce(contactId, (id) => capture('contact_opened', { contact_id: id, source }));

  const contact = detailQuery.data?.contact ?? null;
  const projects: ApiContactProject[] = projectsQuery.data?.projects ?? [];

  const [reportOpen, setReportOpen] = React.useState(false);

  const projectRows = React.useMemo<EvidenceRowItem[]>(() => {
    if (!contact) return [];
    return projects.map((p) => {
      const secondaryNode =
        p.city || p.state || p.roles.length ? (
          <span key={p.project_id} className="flex flex-wrap items-center gap-1.5">
            {p.city || p.state ? (
              <span className="text-muted-foreground">{`${p.city}, ${p.state}`}</span>
            ) : null}
            <ContactRoleBadges roles={p.roles} empty={null} />
          </span>
        ) : (
          '—'
        );
      return {
        key: p.project_id,
        entityKind: 'contact' as const,
        entityId: contact.id,
        projectId: p.project_id,
        entityName: contact.name,
        avatar: (
          <ProjectSourceAvatar
            name={p.project_name ?? p.primary_address}
            latestSource={p.latest_source}
            fallbackIcon={getProjectTypeIcon('other')}
            size="sm"
          />
        ),
        title: p.project_name ?? p.primary_address,
        subtitle: secondaryNode,
        meta: <ApiStageBadge stage={p.stage} />,
        openLabel: 'Open project',
        openIcon: Tag,
        onOpen: () =>
          onNavigate?.(
            { type: 'project', id: p.project_id },
            { seed: apiContactProjectToSeedRow(p), fromLabel: contact.name },
          ),
      };
    });
  }, [contact, onNavigate, projects]);

  const columns = mode === 'expanded' ? 1 : 2;

  if (!contact) {
    // Header keeps close, back, and prev/next reachable while loading or after a failed load.
    const header = (
      <EntityDrawerHeader
        eyebrow="Contact details"
        title={detailQuery.isError ? 'Unable to load contact' : 'Loading contact…'}
        metaIcon={Briefcase}
        metaText=""
        entityIcon={<EntityAvatar alt="" fallbackIcon={User} />}
        onPrev={onPrev}
        onNext={onNext}
        mode={mode}
        onToggleMode={onToggleMode}
        backLabel={backLabel}
        onBack={onBack}
        loading={!detailQuery.isError}
      />
    );
    if (detailQuery.isError) return header;
    return (
      <EntityDetailLayout
        mode={mode}
        header={header}
        summary={<SectionSkeleton rows={5} columns={columns} />}
        tabs={CONTACT_DETAIL_TABS.map((tab) => ({
          ...tab,
          drawerOnly: tab.value === 'overview',
          content: tab.value === 'overview' ? null : <ListSkeleton />,
        }))}
      />
    );
  }

  const metaText = contact.title
    ? `${contact.title} · ${contact.account_name}`
    : contact.account_name;
  const headline =
    contact.headline && headlineAddsSomething(contact.headline, contact.title, contact.account_name)
      ? contact.headline
      : null;
  const fieldColumns = mode === 'expanded' ? 2 : 3;
  const openAccount = onNavigate
    ? () =>
        onNavigate(
          { type: 'account', id: contact.account_id },
          { seed: apiContactDetailToSeedAccount(contact), fromLabel: contact.name },
        )
    : undefined;
  const openContact = onNavigate
    ? (id: string) => onNavigate({ type: 'contact', id }, { fromLabel: contact.name })
    : undefined;
  const colleagues = (colleaguesQuery.data?.contacts ?? []).filter((row) => row.id !== contact.id);
  const colleaguesTable = (
    <AccountContactsTab
      accountId={contact.account_id}
      accountName={contact.account_name}
      accountWebsite={contact.account_website}
      onOpenContact={(id) => openContact?.(id)}
      excludeContactId={contact.id}
      scrollable={mode !== 'expanded'}
      variant="colleagues"
    />
  );
  const expanded = mode === 'expanded';
  const about = <ContactAboutSection contact={contact} />;
  const summary = (
    <>
      {expanded ? about : null}
      <ContactDetailsSection contact={contact} columns={fieldColumns} />
      <ContactAccountSection
        contact={contact}
        workspaceId={workspaceId}
        columns={fieldColumns}
        onOpen={openAccount}
      />
      {expanded ? null : colleaguesTable}
    </>
  );

  return (
    <EnrichScopeProvider workspaceId={workspaceId}>
      <ReportDataIssueDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        workspaceId={workspaceId}
        targetType="contact"
        targetId={contactId}
        targetName={contact.name}
        buildContext={() => buildReportContext({ contact, projects })}
      />
      <EntityDetailLayout
        mode={mode}
        header={
          <EntityDrawerHeader
            eyebrow="Contact details"
            title={contact.name}
            metaIcon={Briefcase}
            metaText={metaText}
            metaSlot={
              headline ? (
                <div className="mt-1 space-y-1 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Briefcase className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{metaText}</span>
                  </div>
                  <p className="line-clamp-2 text-foreground/80">{headline}</p>
                </div>
              ) : undefined
            }
            entityIcon={
              <ContactAvatar
                name={contact.name}
                accountWebsite={contact.account_website}
                email={contact.email}
              />
            }
            action={<ReportIssueButton onClick={() => setReportOpen(true)} />}
            externalUrl={normalizeExternalUrl(contact.linkedin_url)}
            externalLabel="Open LinkedIn profile"
            externalIcon={<AppLogo name="linkedin" size={16} title="LinkedIn" />}
            onPrev={onPrev}
            onNext={onNext}
            mode={mode}
            onToggleMode={onToggleMode}
            backLabel={backLabel}
            onBack={onBack}
          />
        }
        summary={summary}
        tabs={[
          {
            value: 'overview',
            label: 'Overview',
            drawerOnly: true,
            content: expanded || !contact.about ? undefined : about,
          },
          {
            value: 'experience',
            label: 'Experience',
            content: <ContactExperience contact={contact} />,
          },
          // The rail is too narrow for the table: expanded, it gets the main column.
          ...(expanded
            ? [
                {
                  value: 'colleagues',
                  label: 'Colleagues',
                  count: colleagues.length,
                  countLabel: contactsCountShown(
                    colleagues.length,
                    colleaguesQuery.data?.contacts.length,
                  ),
                  content: colleaguesTable,
                },
              ]
            : []),
          {
            value: 'projects',
            label: 'Projects',
            count: contact.projects_count,
            content: projectsQuery.isLoading ? (
              <ListSkeleton />
            ) : projectRows.length ? (
              <ExpandableEvidenceList workspaceId={workspaceId} rows={projectRows} />
            ) : (
              <EmptyTabState message="No related projects" />
            ),
          },
        ]}
      />
    </EnrichScopeProvider>
  );
}

// Stage vocabulary is now canonical on both the API and web; the crosswalk is identity.
const API_STAGE_TO_ROW: Record<NonNullable<ApiContactProject['stage']>, ProjectRow['stage']> = {
  pre_filing: 'pre_filing',
  filed: 'filed',
  under_review: 'under_review',
  approved: 'approved',
  permitted: 'permitted',
  under_construction: 'under_construction',
  completed: 'completed',
};

function ApiStageBadge({ stage }: { stage: ApiContactProject['stage'] }) {
  return <StatusBadge {...stageMeta(stage)} />;
}

// Header seed for the navigated record; the detail query fills in the rest.
export function apiContactProjectToSeedRow(p: ApiContactProject): ProjectRow {
  return {
    id: p.project_id,
    name: p.project_name ?? p.primary_address,
    address: p.primary_address,
    city: p.city,
    state: p.state,
    type: UNKNOWN_PROJECT_TYPE,
    sqft: null,
    stage: p.stage ? API_STAGE_TO_ROW[p.stage] : null,
    disposition: p.disposition,
    developer: '—',
    architect: '—',
    detectedAt: p.last_signal_at ?? new Date().toISOString(),
    source: p.latest_source?.display_name ?? '—',
    sourceBaseUrl: p.latest_source?.base_url ?? null,
    sourceLogoDomain: p.latest_source?.logo_domain ?? null,
    lat: Number.NaN,
    lng: Number.NaN,
    scores: {},
  };
}

const ACCOUNT_TYPE_TO_KIND: Record<ApiAccountType, AccountRow['kind']> = {
  developer: 'developer',
  architect: 'architect',
  engineer: 'engineer',
  gc: 'contractor',
  agency: 'agency',
  law_firm: 'law_firm',
  consultant: 'consultant',
  landscape_architect: 'landscape_architect',
  other: 'unknown',
};

// Header seed for the navigated record; the detail query fills in the rest.
export function apiContactDetailToSeedAccount(c: ApiContactDetail): AccountRow {
  const primary = primaryAccountType(c.account_types);
  const kind: AccountRow['kind'] = primary
    ? (ACCOUNT_TYPE_TO_KIND[primary] ?? 'unknown')
    : 'unknown';
  return {
    id: c.account_id,
    name: c.account_name,
    kind,
    account_types: c.account_types,
    website: c.account_website,
    city: '—',
    state: '—',
    projectsCount: 0,
    contactsCount: 0,
    scores: {},
    lastActivityAt: c.updated_at,
    createdAt: c.created_at,
  };
}
